/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node, Waiting } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    ICheckoutViewProps,
    ILineItem,
    ILineItemDeliveryGroup,
    IOrderSummary,
    IPickUpAtStore,
    ICheckoutModuleProps
} from '@msdyn365-commerce-modules/checkout/src/modules/checkout/./checkout';
import { getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { IEmailDelivery } from '@msdyn365-commerce-modules/checkout/src/modules/checkout/./components';
import { IInvoicePaymentSummary } from '@msdyn365-commerce-modules/checkout/src/modules/checkout/./components/get-invoice-payment-summary';
import { ICheckoutConfig, ICheckoutProps, ICheckoutResources } from '../definition-extensions/checkout.ext.props.autogenerated';
import { ICheckoutSpecificationData } from '../../../data/checkout';
import { Cart, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { parseString } from 'xml2js';
import { LogLevel } from '@msdyn365-commerce/retail-proxy/dist/interfaces/ITelemetry';
import { SelectedConfiguration } from './cart.view';
import { logDetails } from '../../../utils/debug-mode';
import { Heading } from '@msdyn365-commerce-modules/utilities';

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

type LineItemProps = {
    cart: Readonly<Cart>;
    product: SimpleProduct[];
    getPrice: (price: number | undefined) => string;
    getConfigurations: (xml: string) => string;
    routes: any;
    getProductPageUrl: (name: string | undefined, recordId: string | number | undefined) => string;
    resources: ICheckoutResources & ICheckoutResources;
    config: ICheckoutConfig & ICheckoutConfig;
};

export const LineItemsComponent: React.FC<LineItemProps> = ({
    resources,
    cart,
    product,
    getPrice,
    getConfigurations,
    routes,
    getProductPageUrl,
    config
}) => {
    return (
        <div className='ms-checkout__line-items multiple-pickup-enabled'>
            <div className='ms-checkout__line-items-header'>
                {config.lineItemsHeading?.text && (
                    <Heading
                        className='ms-checkout__line-items-heading'
                        text={config.lineItemsHeading.text}
                        headingTag={config.lineItemsHeading?.tag}
                    />
                )}
                <a
                    href={routes.cart.destinationUrl}
                    data-exp-event-id='CheckoutPage.Checkout.Edit cart.click'
                    className='ms-checkout__line-items-edit-cart-link msc-btn btn-link'
                >
                    {resources.editCartLabel}
                </a>
            </div>
            <div className='ms-checkout__line-items-delivery-group multiple-pickup ship'>
                <div className='ms-checkout__line-items-group-title-multiple-pickup-shipicon' />
                <div className='ms-checkout__line-items-group-title-multiple-pickup-heading'>Shipping</div>
                <div className='ms-checkout__line-items-group-title-multiple-pickup-subheading'>({cart.TotalItems} item)</div>
                <div className='ms-checkout__line-items-list'>
                    {cart && cart.CartLines && cart.CartLines?.length > 0
                        ? cart.CartLines.map(line => {
                              return (
                                  <div className='ms-checkout__line-item' key={line.LineId}>
                                      <div className='msc-cart-line'>
                                          <div className='msc-cart-line__product-image'>
                                              <img
                                                  src={
                                                      (line?.ExtensionProperties?.find(prop => prop.Key === 'INSCONFIGURATIONIIMAGE')?.Value
                                                          ?.StringValue as string) ||
                                                      product.find(p => p.RecordId === line.ProductId)?.PrimaryImageUrl
                                                  }
                                                  alt={product.find(p => p.RecordId === line.ProductId)?.Name}
                                              />
                                          </div>
                                          <div className='msc-cart-line__content'>
                                              <div className='msc-cart-line__product'>
                                                  <a
                                                      className='msc-cart-line__product-title'
                                                      data-exp-event-id='CheckoutPage.Checkout.68719564395.click'
                                                      href={getProductPageUrl(
                                                          product.find(p => p.RecordId === line.ProductId)?.Name,
                                                          line.ProductId
                                                      )}
                                                  >
                                                      {product.find(p => p.RecordId === line.ProductId)?.Name}
                                                  </a>
                                                  <div className='msc-cart-line__product-variants'>
                                                      <div className='msc-cart-line__product-variant-item'>
                                                          <span className='msc-cart-line__product-configuration'>
                                                              {getConfigurations(
                                                                  line.ExtensionProperties?.find(
                                                                      prop => prop.Key === 'INSSELECTEDREADABLECONFIGURATION'
                                                                  )?.Value?.StringValue as string
                                                              ).length > 0
                                                                  ? 'Configuration:'
                                                                  : ''}
                                                              <span className='name'>
                                                                  {getConfigurations(
                                                                      line.ExtensionProperties?.find(
                                                                          prop => prop.Key === 'INSSELECTEDREADABLECONFIGURATION'
                                                                      )?.Value?.StringValue as string
                                                                  )}
                                                              </span>
                                                          </span>
                                                      </div>
                                                  </div>
                                                  <div className='msc-cart-line__product-price'>
                                                      <span className='msc-price discount-value'>
                                                          <span className='msc-price__actual' itemProp='price'>
                                                              {getPrice(line.Price)}
                                                          </span>
                                                      </span>
                                                  </div>
                                                  <div className='msc-cart-line__freight' />
                                              </div>
                                              <div className='msc-cart-line-item-product-discount'>
                                                  {line.DiscountLines &&
                                                      line.DiscountLines.length > 0 &&
                                                      line.DiscountLines.map(discount => {
                                                          return (
                                                              <div className='ml-cart-list-item-discount'>
                                                                  <p>
                                                                      {discount.OfferName}: Savings {getPrice(discount.EffectiveAmount)} (
                                                                      {discount.EffectivePercentage}%)
                                                                  </p>
                                                              </div>
                                                          );
                                                      })}
                                              </div>
                                              <div className='msc-cart-line__quantity single-quantity'>
                                                  <label className='quantity-label'>Quantity: {line.Quantity}</label>
                                              </div>
                                          </div>
                                      </div>
                                  </div>
                              );
                          })
                        : null}
                </div>
            </div>
        </div>
    );
};

const OrderSummaryComponent: React.FC<IOrderSummary> = ({ heading, lines }) => (
    <div className='msc-order-summary-wrapper'>
        {heading}
        <div className='msc-order-summary__items'>
            {lines && (
                <>
                    {lines.subtotal}
                    {lines.shipping}
                    {lines.otherCharge}
                    {lines.tax}
                    {lines.totalDiscounts}
                    {lines.loyalty}
                    {lines.customerAccount}
                    {lines.giftCard}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

const PaymentSummaryComponent: React.FC<IInvoicePaymentSummary> = ({ heading, lines }) => (
    <div className='msc-invoice-summary-wrapper'>
        {heading}
        <div className='msc-invoice-summary__items'>
            {lines && (
                <>
                    {lines.invoices}
                    {lines.giftCard}
                    {lines.loyalty}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

const CheckoutView: React.FC<ICheckoutViewProps & ICheckoutProps<ICheckoutModuleProps & ICheckoutSpecificationData>> = props => {
    const {
        isPaymentVerificationRedirection,
        shouldEnableSinglePaymentAuthorizationCheckout,
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        hasInvoiceLine,
        bodyProps,
        mainProps,
        mainControlProps,
        sideProps,
        sideControlFirstProps,
        sideControlSecondProps,
        termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        guidedForm,
        orderSummary,
        invoicePaymentSummary,
        placeOrderButton,
        termsAndConditions,
        keepShoppingButton,
        checkoutExpressPaymentContainer,
        checkoutErrorRef,
        data: { cart, products }
    } = props;
    logDetails(props.context.app.config.debugMode, props);
    const routes = props.app.routes;
    const context = props.context.actionContext;
    const resources = props.resources;

    const getPrice = (price: number | undefined) => {
        if (typeof price !== 'undefined') {
            return props.context.cultureFormatter.formatCurrency(price);
        } else {
            return '';
        }
    };

    const getProductPageUrl = (name: string | undefined, recordId: string | number | undefined) => {
        return getProductPageUrlSync(name as string, recordId as number, context);
    };

    const getConfigurations = (xml: string) => {
        // INSSELECTEDCONFIGURATION
        let configuration = '';
        if (xml) {
            parseString(xml, (err: Error | null, result: SelectedConfiguration) => {
                if (err) {
                    props.context.telemetry.log(LogLevel.Error, err.message);

                    return;
                }

                if (result && result.Session && result.Session.Attribute && result.Session.Attribute.length > 0) {
                    result.Session.Attribute.map(com => {
                        // configuration += `${com.$.name}-`;

                        configuration += `${com.$.readableValue}-`;
                    });
                }
            });
        }

        return configuration.slice(0, -1);
    };

    const user = props.context.request.user;
    const { config } = props;
    return (
        <Module {...checkoutProps} ref={checkoutErrorRef}>
            {!hasSalesOrder && !checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
            {!hasSalesOrder && isPaymentVerificationRedirection && shouldEnableSinglePaymentAuthorizationCheckout && !alert && !loading && (
                <Waiting className='msc-waiting-circular msc-waiting-lg' />
            )}
            {!hasSalesOrder && (
                <Node {...bodyProps}>
                    {loading}
                    {alert}
                    {canShow && (
                        <>
                            <Node {...mainProps}>
                                {!user.isAuthenticated && (
                                    <div className='ml-checkout-verify'>
                                        <label>
                                            <span className='ml-checkout-text'>Sign Me Up!</span>
                                            <br />
                                            <br />
                                            <div style={{ display: 'flex' }}>
                                                <span className='ml-checkout-check'>
                                                    I agree to have Morland send me their latest news via printed material and email
                                                </span>
                                                <input type='checkbox' className='ml-checkout-newsletter-checkbox' />
                                            </div>
                                        </label>
                                    </div>
                                )}
                                {!config.hideCollectionOption && (
                                    <div className='ml-checkout-message'>
                                        <p>
                                            IF YOU ARE OPTING TO COLLECT IN STORE THEN THE DELIVERY ADDRESS WILL BE USED AS YOUR BILLING
                                            ADDRESS
                                        </p>
                                    </div>
                                )}
                                {checkoutExpressPaymentContainer}
                                {checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
                                {guidedForm}
                                <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                <Node {...mainControlProps}>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                            <Node {...sideProps}>
                                {!hasInvoiceLine
                                    ? orderSummary && <OrderSummaryComponent {...orderSummary} />
                                    : invoicePaymentSummary && <PaymentSummaryComponent {...invoicePaymentSummary} />}
                                <Node {...sideControlFirstProps}>
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                                {cart.result?.cart && products.result && (
                                    <LineItemsComponent
                                        cart={cart.result?.cart}
                                        product={products.result}
                                        getPrice={getPrice}
                                        getConfigurations={getConfigurations}
                                        routes={routes}
                                        getProductPageUrl={getProductPageUrl}
                                        resources={resources}
                                        config={config}
                                    />
                                )}
                                <Node {...sideControlSecondProps}>
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                        </>
                    )}
                </Node>
            )}
            {hasSalesOrder && orderConfirmation}
        </Module>
    );
};

export default CheckoutView;
